/**
 * VerdictRuleBuilder component - Visual verdict rule editor
 *
 * Per CONTEXT.md locked decision: full editable rule engine with priority-ordered
 * rules, condition groups, AND/OR logic, and context-sensitive dropdowns.
 *
 * "Everything should be expandable/inspectable (no black boxes),
 *  the editor should teach you how the system works."
 *
 * Rules are evaluated top-to-bottom (priority order). First matching rule wins.
 * Each rule has condition groups connected by AND/OR logic.
 * Each group has conditions connected by AND/OR logic.
 * Conditions reference gates, gate aggregates, diagnosis dimensions, or KPIs.
 */

import * as Popover from '@radix-ui/react-popover';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  GripVertical,
  HelpCircle,
  Plus,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { KPI_OPTIONS } from '@/constants/kpi-options';
import type {
  Condition,
  ConditionGroup,
  ConditionOperator,
  ConditionSource,
  DiagnosisDimension,
  Gate,
  KpiOption,
  PercentileSettings,
  VerdictRule,
} from '@/types/template';

import { RuleSettingsBar } from './RuleSettingsBar';

// ============================================================================
// TYPES
// ============================================================================

export interface VerdictRuleBuilderProps {
  rules: VerdictRule[];
  onChange: (rules: VerdictRule[]) => void;
  gates: Gate[];
  dimensions: DiagnosisDimension[];
  /** KPI IDs selected as primary/secondary in KpiConfig — only these appear in the KPI source dropdown */
  selectedKpiIds?: string[];
  /** Active team Custom KPI options merged with built-in KPI options */
  customKpiOptions?: KpiOption[];
  /** Template-level percentile settings (minimum peers, default scope) */
  percentileSettings?: PercentileSettings;
  /** Callback when percentile settings change */
  onPercentileSettingsChange?: (settings: PercentileSettings) => void;
  /** Template-level rolling window (1-90 days, default 7). Phase 29. */
  rollingWindowDays?: number;
  /** Callback when rolling window changes */
  onRollingWindowDaysChange?: (days: number) => void;
}

type Verdict = 'Scale' | 'Fix' | 'Kill';

// ============================================================================
// HELPERS
// ============================================================================

function uid(): string {
  return crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 11);
}

interface OperatorOption {
  value: ConditionOperator;
  label: string;
  tooltip: string;
  group?: 'Absolute' | 'Relative (vs peers)';
}

function getOperatorsForSource(
  source: ConditionSource,
  dimensions: DiagnosisDimension[],
  kpiId?: string,
  kpiOptions: KpiOption[] = KPI_OPTIONS,
): OperatorOption[] {
  if (source.type === 'gate') {
    return [
      { value: 'fails', label: 'fails', tooltip: 'True when this gate evaluates to FAIL' },
      { value: 'passes', label: 'passes', tooltip: 'True when this gate evaluates to PASS' },
    ];
  }

  if (source.type === 'gateAggregate') {
    if (source.aggregate === 'count') {
      return [
        { value: 'greaterThan', label: 'greater than', tooltip: 'Count of failing gates exceeds this number' },
        { value: 'lessThan', label: 'less than', tooltip: 'Count of failing gates is below this number' },
        { value: 'equals', label: 'equals', tooltip: 'Count of failing gates equals this number exactly' },
      ];
    }
    return [
      { value: 'fails', label: 'fails', tooltip: `True when ${source.aggregate === 'any' ? 'at least one' : 'every'} gate fails` },
      { value: 'passes', label: 'passes', tooltip: `True when ${source.aggregate === 'any' ? 'at least one' : 'every'} gate passes` },
    ];
  }

  if (source.type === 'diagnosis') {
    const dim = dimensions.find((d) => d.dimensionId === source.dimensionId);
    if (!dim) {
      return [{ value: 'equals', label: 'equals', tooltip: 'Exact match comparison' }];
    }
    const outputType = dim.outputConfig.type;
    if (outputType === 'binary') {
      return [{ value: 'equals', label: 'equals', tooltip: 'True when dimension result matches yes/no value' }];
    }
    if (outputType === 'scale') {
      return [
        { value: 'greaterThan', label: 'greater than', tooltip: 'True when dimension score exceeds this value' },
        { value: 'lessThan', label: 'less than', tooltip: 'True when dimension score is below this value' },
        { value: 'equals', label: 'equals', tooltip: 'True when dimension score exactly matches' },
      ];
    }
    if (outputType === 'options') {
      if (dim.outputConfig.allowMultiple) {
        return [{ value: 'contains', label: 'contains', tooltip: 'True when selected options include this value' }];
      }
      return [{ value: 'equals', label: 'equals', tooltip: 'True when selected option matches' }];
    }
  }

  if (source.type === 'kpi') {
    const isCostMetric = kpiId
      ? kpiOptions.find((kpi) => kpi.id === kpiId)?.higherIsBetter === false
      : false;
    const aboveLabel = isCostMetric ? 'best %' : 'top %';
    const belowLabel = isCostMetric ? 'worst %' : 'bottom %';
    return [
      { value: 'greaterThan', label: 'greater than', tooltip: 'True when KPI value exceeds threshold', group: 'Absolute' },
      { value: 'lessThan', label: 'less than', tooltip: 'True when KPI value is below threshold', group: 'Absolute' },
      { value: 'equals', label: 'equals', tooltip: 'True when KPI value matches exactly', group: 'Absolute' },
      { value: 'percentileAbove', label: aboveLabel, tooltip: 'Compares against other creatives in this project. Requires minimum peers.', group: 'Relative (vs peers)' },
      { value: 'percentileBelow', label: belowLabel, tooltip: 'Compares against other creatives in this project. Requires minimum peers.', group: 'Relative (vs peers)' },
    ];
  }

  return [{ value: 'equals', label: 'equals', tooltip: 'Exact match comparison' }];
}

function getDefaultOperatorForSource(
  source: ConditionSource,
  dimensions: DiagnosisDimension[],
): ConditionOperator {
  const ops = getOperatorsForSource(source, dimensions);
  return ops[0]?.value ?? 'equals';
}

function getDefaultValueForSource(
  source: ConditionSource,
  dimensions: DiagnosisDimension[],
): string | number | boolean {
  if (source.type === 'gate') return true;
  if (source.type === 'gateAggregate') {
    if (source.aggregate === 'count') return 0;
    return true;
  }
  if (source.type === 'diagnosis') {
    const dim = dimensions.find((d) => d.dimensionId === source.dimensionId);
    if (dim) {
      if (dim.outputConfig.type === 'binary') return true;
      if (dim.outputConfig.type === 'scale') return 0;
      if (dim.outputConfig.type === 'options') {
        return dim.outputConfig.options?.[0] ?? '';
      }
    }
    return '';
  }
  if (source.type === 'kpi') return 0;
  return '';
}

function needsValueInput(source: ConditionSource, _operator: ConditionOperator): boolean {
  if (source.type === 'gate') return false;
  if (source.type === 'gateAggregate' && source.aggregate !== 'count') return false;
  return true;
}

function isPercentileOperator(op: ConditionOperator): boolean {
  return op === 'percentileAbove' || op === 'percentileBelow';
}

// Preset chips for percentile value selection
interface PercentilePreset {
  label: string;
  /** The threshold value stored on the condition (0-100 percentile rank) */
  valueForAbove: number;
  valueForBelow: number;
}

const PERCENTILE_PRESETS: PercentilePreset[] = [
  { label: 'Top 25%', valueForAbove: 75, valueForBelow: 25 },
  { label: 'Top 50%', valueForAbove: 50, valueForBelow: 50 },
  { label: 'Bottom 25%', valueForAbove: 25, valueForBelow: 75 },
  { label: 'Bottom 50%', valueForAbove: 50, valueForBelow: 50 },
];

function getPresetValue(preset: PercentilePreset, operator: ConditionOperator): number {
  return operator === 'percentileAbove' ? preset.valueForAbove : preset.valueForBelow;
}

/**
 * Returns a direction-aware label for a percentile preset.
 * Cost metrics (higherIsBetter=false) use "Best/Worst" instead of "Top/Bottom"
 * to avoid ambiguity (e.g., "Top 25% CPC" could mean cheapest or most expensive).
 */
function getPresetLabel(
  preset: PercentilePreset,
  kpiId: string | undefined,
  kpiOptions: KpiOption[],
): string {
  if (!kpiId) return preset.label;
  const kpi = kpiOptions.find((option) => option.id === kpiId);
  if (!kpi || kpi.higherIsBetter) return preset.label;
  // Cost metrics: lowest value = best performance
  return preset.label.replace('Top', 'Best').replace('Bottom', 'Worst');
}

const SCOPE_LABELS: Record<string, string> = {
  smart: 'Smart',
  goal: 'By Goal',
  bucket: 'By Objective',
  all: 'All Project',
};

const SCOPE_OPTIONS: { value: 'smart' | 'goal' | 'bucket' | 'all'; label: string }[] = [
  { value: 'smart', label: 'Smart default' },
  { value: 'goal', label: 'By Goal' },
  { value: 'bucket', label: 'By Objective' },
  { value: 'all', label: 'All Project' },
];

// ============================================================================
// VERDICT STYLES
// ============================================================================

const VERDICT_STYLES: Record<Verdict, string> = {
  Kill: 'bg-danger/10 text-danger border-danger/20',
  Fix: 'bg-warning/10 text-warning border-warning/20',
  Scale: 'bg-success/10 text-success border-success/20',
};

const VERDICT_BORDER_ACCENT: Record<Verdict, string> = {
  Kill: 'border-l-danger',
  Fix: 'border-l-warning',
  Scale: 'border-l-success',
};

// ============================================================================
// PERCENTILE VALUE CHIPS
// ============================================================================

interface PercentileValueChipsProps {
  condition: Condition;
  onChange: (updated: Condition) => void;
  kpiId?: string;
  kpiOptions: KpiOption[];
  defaultScope?: 'smart' | 'goal' | 'bucket' | 'all';
}

function PercentileValueChips({
  condition,
  onChange,
  kpiId,
  kpiOptions,
  defaultScope = 'smart',
}: PercentileValueChipsProps) {
  // Track selected preset by index to avoid dual-highlight bug
  // (Top 50% and Bottom 50% both map to value 50 for percentileAbove)
  const [selectedPresetIndex, setSelectedPresetIndex] = useState<number | null>(() => {
    const currentVal = typeof condition.value === 'number' ? condition.value : 0;
    const idx = PERCENTILE_PRESETS.findIndex(
      (p) => getPresetValue(p, condition.operator) === currentVal,
    );
    return idx >= 0 ? idx : null;
  });

  const [isCustom, setIsCustom] = useState(() => {
    return selectedPresetIndex === null;
  });

  const currentValue = typeof condition.value === 'number' ? condition.value : 0;
  const currentScope =
    condition.source.type === 'kpi' ? (condition.source.scope ?? 'smart') : 'smart';

  const chipBase =
    'px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer transition-colors whitespace-nowrap';
  const chipActive = 'bg-brand/15 text-brand border-brand/30';
  const chipInactive =
    'bg-surface-subtle text-secondary border-border-subtle hover:border-brand/20 hover:text-primary';

  // When operator changes (percentileAbove <-> percentileBelow), recalculate value
  // to keep the preset identity stable (e.g., "Top 25%" stays selected)
  useEffect(() => {
    if (selectedPresetIndex !== null && PERCENTILE_PRESETS[selectedPresetIndex]) {
      const newVal = getPresetValue(PERCENTILE_PRESETS[selectedPresetIndex], condition.operator);
      if (typeof condition.value === 'number' && condition.value !== newVal) {
        onChange({ ...condition, value: newVal });
      }
    }
    // Only react to operator changes, not condition object identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition.operator]);

  const handlePresetClick = (preset: PercentilePreset, index: number) => {
    setIsCustom(false);
    setSelectedPresetIndex(index);
    const val = getPresetValue(preset, condition.operator);
    onChange({ ...condition, value: val });
  };

  const handleCustomClick = () => {
    setIsCustom(true);
    setSelectedPresetIndex(null);
  };

  const handleCustomValueChange = (val: number) => {
    const clamped = Math.min(100, Math.max(0, val));
    onChange({ ...condition, value: clamped });
  };

  const handleScopeChange = (scope: 'smart' | 'goal' | 'bucket' | 'all') => {
    if (condition.source.type === 'kpi') {
      onChange({
        ...condition,
        source: { ...condition.source, scope },
      });
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* Preset chips */}
      {PERCENTILE_PRESETS.map((preset, index) => {
        const isActive = !isCustom && selectedPresetIndex === index;
        const displayLabel = getPresetLabel(preset, kpiId, kpiOptions);
        return (
          <button
            key={preset.label}
            type="button"
            className={`${chipBase} ${isActive ? chipActive : chipInactive}`}
            onClick={() => handlePresetClick(preset, index)}
          >
            {displayLabel}
          </button>
        );
      })}

      {/* Custom chip + input */}
      <button
        type="button"
        className={`${chipBase} ${isCustom ? chipActive : chipInactive}`}
        onClick={handleCustomClick}
      >
        Custom
      </button>
      {isCustom && (
        <div className="flex items-center gap-0.5">
          <input
            type="number"
            className="border border-border-subtle rounded-lg px-1.5 py-0.5 text-xs bg-surface w-14 focus:outline-none focus:ring-1 focus:ring-brand text-primary text-center"
            min={0}
            max={100}
            value={currentValue}
            onChange={(e) =>
              handleCustomValueChange(e.target.value === '' ? 0 : Number(e.target.value))
            }
          />
          <span className="text-xs text-muted">%</span>
        </div>
      )}

      {/* Scope chip — shows inherited vs overridden styling */}
      <Popover.Root>
        <Popover.Trigger asChild>
          {currentScope === defaultScope ? (
            <button
              type="button"
              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-brand/10 text-brand border border-brand/20 cursor-pointer hover:bg-brand/15 transition-colors"
              title="Override the default scope for this condition"
            >
              {SCOPE_LABELS[currentScope] ?? 'Smart'} <span className="opacity-60">(default)</span>
              <ChevronDown className="w-2.5 h-2.5" />
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-brand/10 text-brand border border-brand/35 ring-1 ring-brand/20 cursor-pointer hover:bg-brand/15 transition-colors"
              title="Override the default scope for this condition"
            >
              {SCOPE_LABELS[currentScope] ?? 'Smart'}
              <ChevronDown className="w-2.5 h-2.5" />
            </button>
          )}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="bg-surface border border-border-default rounded-lg shadow-lg p-1 z-50 min-w-[140px]"
            sideOffset={4}
            align="start"
          >
            {SCOPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-xs rounded transition-colors ${
                  currentScope === opt.value
                    ? 'bg-brand/10 text-brand font-medium'
                    : 'text-secondary hover:bg-surface-subtle hover:text-primary'
                }`}
                onClick={() => handleScopeChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

// ============================================================================
// CONDITION ROW
// ============================================================================

interface ConditionRowProps {
  condition: Condition;
  gates: Gate[];
  dimensions: DiagnosisDimension[];
  selectedKpiIds?: string[];
  kpiOptions: KpiOption[];
  defaultScope?: 'smart' | 'goal' | 'bucket' | 'all';
  onChange: (updated: Condition) => void;
  onDelete: () => void;
}

function ConditionRow({
  condition,
  gates,
  dimensions,
  selectedKpiIds,
  kpiOptions,
  defaultScope,
  onChange,
  onDelete,
}: ConditionRowProps) {
  const conditionKpiId = condition.source.type === 'kpi' ? condition.source.kpiId : undefined;
  const operators = getOperatorsForSource(condition.source, dimensions, conditionKpiId, kpiOptions);
  const showValue = needsValueInput(condition.source, condition.operator);

  // Determine value input type
  let valueInputType: 'none' | 'number' | 'boolean' | 'select' = 'none';
  let selectOptions: string[] = [];
  let unitSuffix = '';

  if (showValue) {
    if (condition.source.type === 'gateAggregate' && condition.source.aggregate === 'count') {
      valueInputType = 'number';
    } else if (condition.source.type === 'diagnosis') {
      const diagSource = condition.source;
      const dim = dimensions.find((d) => d.dimensionId === diagSource.dimensionId);
      if (dim) {
        const ot = dim.outputConfig.type;
        if (ot === 'binary') {
          valueInputType = 'boolean';
        } else if (ot === 'scale') {
          valueInputType = 'number';
        } else if (ot === 'options') {
          valueInputType = 'select';
          selectOptions = dim.outputConfig.options ?? [];
        }
      }
    } else if (condition.source.type === 'kpi') {
      const kpiSource = condition.source;
      valueInputType = 'number';
      const kpi = kpiOptions.find((k) => k.id === kpiSource.kpiId);
      if (kpi) unitSuffix = kpi.unit;
    }
  }

  const enabledDimensions = dimensions.filter((d) => d.enabled);

  // Filter KPIs to only those selected in KpiConfig (primary + secondary)
  const availableKpis = selectedKpiIds && selectedKpiIds.length > 0
    ? kpiOptions.filter((kpi) => selectedKpiIds.includes(kpi.id))
    : kpiOptions;

  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSource: ConditionSource = JSON.parse(e.target.value);
    const newOp = getDefaultOperatorForSource(newSource, dimensions);
    const newVal = getDefaultValueForSource(newSource, dimensions);
    onChange({ ...condition, source: newSource, operator: newOp, value: newVal });
  };

  const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...condition, operator: e.target.value as ConditionOperator });
  };

  const handleValueChange = (val: string | number | boolean) => {
    onChange({ ...condition, value: val });
  };

  const selectClass =
    'border border-border-subtle rounded-lg px-2 py-1.5 text-sm bg-surface min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand text-primary';
  const inputClass =
    'border border-border-subtle rounded-lg px-2 py-1.5 text-sm bg-surface w-24 focus:outline-none focus:ring-1 focus:ring-brand text-primary';

  return (
    <div className="flex items-center gap-2 py-1 flex-wrap">
      {/* NOT toggle */}
      <button
        type="button"
        onClick={() => onChange({ ...condition, negated: !condition.negated })}
        className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded border transition-colors flex-shrink-0 ${
          condition.negated
            ? 'bg-danger/10 text-danger border-danger/20'
            : 'bg-surface-subtle text-muted border-border-subtle hover:text-secondary'
        }`}
        title="Toggle NOT — negate this condition"
      >
        NOT
      </button>

      {/* Source dropdown */}
      <select
        aria-label="Condition source"
        className={selectClass}
        value={JSON.stringify(condition.source)}
        onChange={handleSourceChange}
      >
        <optgroup label="Gates">
          {gates.map((g) => (
            <option key={g.gateId} value={JSON.stringify({ type: 'gate', gateId: g.gateId })}>
              {g.name || '(unnamed gate)'}
            </option>
          ))}
          <option value={JSON.stringify({ type: 'gateAggregate', aggregate: 'any' })}>
            Any gate
          </option>
          <option
            value={JSON.stringify({ type: 'gateAggregate', aggregate: 'any', role: 'critical' })}
          >
            Any critical gate
          </option>
          <option
            value={JSON.stringify({ type: 'gateAggregate', aggregate: 'any', role: 'standard' })}
          >
            Any standard gate
          </option>
          <option value={JSON.stringify({ type: 'gateAggregate', aggregate: 'all' })}>
            All gates
          </option>
          <option value={JSON.stringify({ type: 'gateAggregate', aggregate: 'count' })}>
            Count of failing gates
          </option>
        </optgroup>
        {enabledDimensions.length > 0 && (
          <optgroup label="Diagnosis">
            {enabledDimensions.map((d) => (
              <option
                key={d.dimensionId}
                value={JSON.stringify({ type: 'diagnosis', dimensionId: d.dimensionId })}
              >
                {d.name}
              </option>
            ))}
          </optgroup>
        )}
        {availableKpis.length > 0 && (
          <optgroup label="Performance KPIs">
            {availableKpis.map((kpi) => (
              <option
                key={kpi.id}
                value={JSON.stringify({ type: 'kpi', kpiId: kpi.id })}
              >
                {kpi.shortLabel} ({kpi.unit})
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {/* Operator dropdown with tooltip — uses optgroup when operators have groups */}
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-1">
          {(() => {
            const hasGroups = operators.some((op) => op.group);
            if (hasGroups) {
              // Group operators by their group property
              const groups = new Map<string, OperatorOption[]>();
              for (const op of operators) {
                const key = op.group ?? 'Other';
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(op);
              }
              return (
                <select
                  className={selectClass}
                  style={{ minWidth: '120px' }}
                  value={condition.operator}
                  onChange={handleOperatorChange}
                >
                  {Array.from(groups.entries()).map(([groupLabel, ops]) => (
                    <optgroup key={groupLabel} label={groupLabel}>
                      {ops.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              );
            }
            return (
              <select
                className={selectClass}
                style={{ minWidth: '120px' }}
                value={condition.operator}
                onChange={handleOperatorChange}
              >
                {operators.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            );
          })()}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="p-0.5 text-muted hover:text-secondary"
                aria-label="Operator help"
              >
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {operators.find((op) => op.value === condition.operator)?.tooltip ?? 'Comparison operator'}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      {/* Percentile value: preset chips + custom input + scope chip */}
      {showValue && isPercentileOperator(condition.operator) && (
        <PercentileValueChips
          condition={condition}
          onChange={onChange}
          kpiId={condition.source.type === 'kpi' ? condition.source.kpiId : undefined}
          kpiOptions={kpiOptions}
          defaultScope={defaultScope}
        />
      )}

      {/* Standard number value input (non-percentile) */}
      {showValue && valueInputType === 'number' && !isPercentileOperator(condition.operator) && (
        <div className="flex items-center gap-1">
          <input
            type="number"
            className={inputClass}
            value={typeof condition.value === 'number' ? condition.value : ''}
            onChange={(e) => handleValueChange(e.target.value === '' ? 0 : Number(e.target.value))}
          />
          {unitSuffix && <span className="text-xs text-muted">{unitSuffix}</span>}
        </div>
      )}

      {showValue && valueInputType === 'boolean' && (
        <select
          className={selectClass}
          style={{ minWidth: '80px' }}
          value={String(condition.value)}
          onChange={(e) => handleValueChange(e.target.value === 'true')}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      )}

      {showValue && valueInputType === 'select' && (
        <select
          className={selectClass}
          style={{ minWidth: '120px' }}
          value={String(condition.value)}
          onChange={(e) => handleValueChange(e.target.value)}
        >
          {selectOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}

      {/* Delete condition */}
      <button
        type="button"
        onClick={onDelete}
        className="p-1 text-muted hover:text-danger transition-colors rounded flex-shrink-0"
        title="Remove condition"
        aria-label="Remove condition"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ============================================================================
// RULE CARD
// ============================================================================

interface RuleCardProps {
  rule: VerdictRule;
  index: number;
  totalRules: number;
  gates: Gate[];
  dimensions: DiagnosisDimension[];
  selectedKpiIds?: string[];
  kpiOptions: KpiOption[];
  defaultScope?: 'smart' | 'goal' | 'bucket' | 'all';
  onChange: (updated: VerdictRule) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isDefault: boolean;
}

function RuleCard({
  rule,
  index,
  totalRules,
  gates,
  dimensions,
  selectedKpiIds,
  kpiOptions,
  defaultScope,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  isDefault,
}: RuleCardProps) {
  const updateCondition = (groupIdx: number, condIdx: number, updated: Condition) => {
    const newGroups = rule.conditionGroups.map((g, gi) => {
      if (gi !== groupIdx) return g;
      return {
        ...g,
        conditions: g.conditions.map((c, ci) => (ci === condIdx ? updated : c)),
      };
    });
    onChange({ ...rule, conditionGroups: newGroups });
  };

  const deleteCondition = (groupIdx: number, condIdx: number) => {
    const newGroups = rule.conditionGroups
      .map((g, gi) => {
        if (gi !== groupIdx) return g;
        return {
          ...g,
          conditions: g.conditions.filter((_, ci) => ci !== condIdx),
        };
      })
      .filter((g) => g.conditions.length > 0);
    onChange({ ...rule, conditionGroups: newGroups });
  };

  const addCondition = (groupIdx: number) => {
    const defaultSource: ConditionSource =
      gates.length > 0
        ? { type: 'gate', gateId: gates[0]!.gateId }
        : { type: 'gateAggregate', aggregate: 'any' };
    const newCondition: Condition = {
      id: uid(),
      source: defaultSource,
      operator: 'fails',
      value: true,
    };
    const newGroups = rule.conditionGroups.map((g, gi) => {
      if (gi !== groupIdx) return g;
      return { ...g, conditions: [...g.conditions, newCondition] };
    });
    onChange({ ...rule, conditionGroups: newGroups });
  };

  const addConditionGroup = () => {
    const defaultSource: ConditionSource =
      gates.length > 0
        ? { type: 'gate', gateId: gates[0]!.gateId }
        : { type: 'gateAggregate', aggregate: 'any' };
    const newGroup: ConditionGroup = {
      id: uid(),
      logic: 'AND',
      conditions: [
        {
          id: uid(),
          source: defaultSource,
          operator: 'fails',
          value: true,
        },
      ],
    };
    onChange({ ...rule, conditionGroups: [...rule.conditionGroups, newGroup] });
  };

  const toggleGroupLogic = (groupIdx: number) => {
    const newGroups = rule.conditionGroups.map((g, gi) => {
      if (gi !== groupIdx) return g;
      return { ...g, logic: g.logic === 'AND' ? ('OR' as const) : ('AND' as const) };
    });
    onChange({ ...rule, conditionGroups: newGroups });
  };

  const toggleGroupsLogic = () => {
    onChange({
      ...rule,
      groupsLogic: rule.groupsLogic === 'AND' ? 'OR' : 'AND',
    });
  };

  return (
    <div
      className={`bg-surface rounded-xl border border-border-default border-l-4 ${VERDICT_BORDER_ACCENT[rule.verdict]} p-4 relative`}
    >
      {/* Delete button -- top right (hidden for fallback rule) */}
      {!(isDefault && rule.conditionGroups.length === 0) && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute top-3 right-3 p-1 text-muted hover:text-danger transition-colors rounded"
          title="Delete rule"
          aria-label="Delete rule"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}

      <div className="flex items-start gap-3">
        {/* Left side: priority + move controls */}
        <div className="flex flex-col items-center gap-1 pt-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0 || (isDefault && rule.conditionGroups.length === 0)}
            className="p-0.5 text-muted hover:text-primary disabled:opacity-20 transition-colors"
            title="Move up (higher priority)"
            aria-label="Move rule up"
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <div className="w-6 h-6 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center">
            {isDefault && rule.conditionGroups.length === 0 ? '\u2193' : index + 1}
          </div>
          <GripVertical className="w-4 h-4 text-muted/30" />
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === totalRules - 1 || (isDefault && rule.conditionGroups.length === 0)}
            className="p-0.5 text-muted hover:text-primary disabled:opacity-20 transition-colors"
            title="Move down (lower priority)"
            aria-label="Move rule down"
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 pr-8">
          {/* Header: verdict badge + label */}
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${VERDICT_STYLES[rule.verdict]}`}
            >
              {rule.verdict}
            </span>
            {isDefault && rule.conditionGroups.length === 0 && (
              <span className="text-xs font-medium text-muted bg-surface-subtle px-1.5 py-0.5 rounded">
                Default (fallback)
              </span>
            )}
          </div>

          {/* Label input */}
          <input
            type="text"
            className="text-xs text-muted bg-transparent border-none outline-none w-full mb-2 placeholder:text-muted/50"
            placeholder="Rule label (optional, e.g. 'Critical gate failure')"
            value={rule.label ?? ''}
            onChange={(e) => onChange({ ...rule, label: e.target.value || undefined })}
          />

          {/* Condition groups */}
          {rule.conditionGroups.length === 0 ? (
            <p className="text-sm text-muted italic">
              {isDefault
                ? 'This is the default fallback — it matches when no other rule applies.'
                : 'No conditions — this rule matches everything (fallback). Add a condition to make it conditional.'}
            </p>
          ) : (
            <div className="space-y-2">
              {rule.conditionGroups.map((group, groupIdx) => (
                <div key={group.id}>
                  {/* Group connector -- between groups */}
                  {groupIdx > 0 && (
                    <div className="flex items-center justify-center my-2">
                      <button
                        type="button"
                        onClick={toggleGroupsLogic}
                        className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-brand/10 text-brand cursor-pointer hover:bg-brand/20 transition-colors"
                      >
                        {rule.groupsLogic}
                      </button>
                    </div>
                  )}

                  {/* Condition group box */}
                  <div className="bg-canvas rounded-lg border border-border-subtle p-3 space-y-1">
                    {group.conditions.map((cond, condIdx) => (
                      <div key={cond.id}>
                        {/* AND/OR toggle between conditions */}
                        {condIdx > 0 && (
                          <div className="flex items-center my-0.5 ml-8">
                            <button
                              type="button"
                              onClick={() => toggleGroupLogic(groupIdx)}
                              className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-brand/10 text-brand cursor-pointer hover:bg-brand/20 transition-colors"
                            >
                              {group.logic}
                            </button>
                          </div>
                        )}
                        <ConditionRow
                          condition={cond}
                          gates={gates}
                          dimensions={dimensions}
                          selectedKpiIds={selectedKpiIds}
                          kpiOptions={kpiOptions}
                          defaultScope={defaultScope}
                          onChange={(updated) => updateCondition(groupIdx, condIdx, updated)}
                          onDelete={() => deleteCondition(groupIdx, condIdx)}
                        />
                      </div>
                    ))}

                    {/* Add condition inside group */}
                    <button
                      type="button"
                      onClick={() => addCondition(groupIdx)}
                      className="flex items-center gap-1 text-xs text-brand hover:text-brand-hover mt-1 transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      Add condition
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add condition group */}
          <button
            type="button"
            onClick={addConditionGroup}
            className="flex items-center gap-1 text-xs text-brand hover:text-brand-hover mt-2 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Add condition group
          </button>
        </div>

        {/* Right side: verdict selector */}
        <div className="flex-shrink-0">
          <select
            className={`text-sm font-semibold rounded-lg px-3 py-1.5 border cursor-pointer appearance-none text-center ${VERDICT_STYLES[rule.verdict]}`}
            value={rule.verdict}
            onChange={(e) =>
              onChange({ ...rule, verdict: e.target.value as Verdict })
            }
          >
            <option value="Kill">Kill</option>
            <option value="Fix">Fix</option>
            <option value="Scale">Scale</option>
          </select>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function VerdictRuleBuilder({
  rules,
  onChange,
  gates,
  dimensions,
  selectedKpiIds,
  customKpiOptions = [],
  percentileSettings,
  onPercentileSettingsChange,
  rollingWindowDays,
  onRollingWindowDaysChange,
}: VerdictRuleBuilderProps) {
  const kpiOptions = useMemo(
    () => [...KPI_OPTIONS, ...customKpiOptions],
    [customKpiOptions],
  );

  // Detect if any rule uses percentile operators (self-revealing pattern)
  const hasPercentileOperators = useMemo(
    () =>
      rules.some((rule) =>
        rule.conditionGroups.some((group) =>
          group.conditions.some((c) => isPercentileOperator(c.operator)),
        ),
      ),
    [rules],
  );

  // Provide default settings when none exist but percentile operators are present
  const effectiveSettings: PercentileSettings = percentileSettings ?? {
    minimumPeers: 5,
    defaultScope: 'smart',
  };
  // Ensure a fallback rule (empty conditions) always exists.
  // If no rule has empty conditionGroups, inject one on first render.
  const ensuredRules = useMemo(() => {
    const hasFallback = rules.some((r) => r.conditionGroups.length === 0);
    if (hasFallback) return rules;
    const maxPriority = rules.reduce((max, r) => Math.max(max, r.priority), 0);
    return [
      ...rules,
      {
        id: uid(),
        priority: maxPriority + 1,
        verdict: 'Fix' as const,
        groupsLogic: 'AND' as const,
        conditionGroups: [],
        label: 'Default / Fallback',
      },
    ];
  }, [rules]);

  // Emit the ensured rules if we had to add a fallback
  useEffect(() => {
    if (ensuredRules !== rules && ensuredRules.length !== rules.length) {
      onChange(ensuredRules);
    }
  }, [ensuredRules, rules, onChange]);

  const sortedRules = [...ensuredRules].sort((a, b) => a.priority - b.priority);

  // The fallback rule is the one with empty conditionGroups (should be last due to highest priority number)
  const defaultRuleId = sortedRules.find((r) => r.conditionGroups.length === 0)?.id ?? null;

  const handleRuleChange = useCallback(
    (ruleId: string, updated: VerdictRule) => {
      onChange(rules.map((r) => (r.id === ruleId ? updated : r)));
    },
    [rules, onChange],
  );

  const handleRuleDelete = useCallback(
    (ruleId: string) => {
      // Prevent deleting the fallback rule
      if (ruleId === defaultRuleId) return;
      onChange(rules.filter((r) => r.id !== ruleId));
    },
    [rules, onChange, defaultRuleId],
  );

  const handleMoveUp = useCallback(
    (ruleId: string) => {
      // Fallback rule cannot be moved
      if (ruleId === defaultRuleId) return;
      const sorted = [...rules].sort((a, b) => a.priority - b.priority);
      const idx = sorted.findIndex((r) => r.id === ruleId);
      if (idx <= 0) return;
      // Swap priorities
      const current = sorted[idx]!;
      const above = sorted[idx - 1]!;
      const tempPriority = current.priority;
      const updated = rules.map((r) => {
        if (r.id === current.id) return { ...r, priority: above.priority };
        if (r.id === above.id) return { ...r, priority: tempPriority };
        return r;
      });
      onChange(updated);
    },
    [rules, onChange, defaultRuleId],
  );

  const handleMoveDown = useCallback(
    (ruleId: string) => {
      // Fallback rule cannot be moved; also prevent moving a rule below the fallback
      if (ruleId === defaultRuleId) return;
      const sorted = [...rules].sort((a, b) => a.priority - b.priority);
      const idx = sorted.findIndex((r) => r.id === ruleId);
      if (idx < 0 || idx >= sorted.length - 1) return;
      const below = sorted[idx + 1]!;
      // Don't swap with the fallback rule
      if (below.id === defaultRuleId) return;
      const current = sorted[idx]!;
      const tempPriority = current.priority;
      const updated = rules.map((r) => {
        if (r.id === current.id) return { ...r, priority: below.priority };
        if (r.id === below.id) return { ...r, priority: tempPriority };
        return r;
      });
      onChange(updated);
    },
    [rules, onChange, defaultRuleId],
  );

  const handleAddRule = useCallback(() => {
    // Find the fallback rule's priority so the new rule goes just before it
    const fallbackRule = rules.find((r) => r.id === defaultRuleId);
    const fallbackPriority = fallbackRule?.priority ?? rules.reduce((max, r) => Math.max(max, r.priority), 0) + 1;

    // Insert new rule just before the fallback, then bump fallback priority
    const defaultSource: ConditionSource =
      gates.length > 0
        ? { type: 'gate', gateId: gates[0]!.gateId }
        : { type: 'gateAggregate', aggregate: 'any' };
    const newRule: VerdictRule = {
      id: uid(),
      priority: fallbackPriority,
      verdict: 'Fix',
      groupsLogic: 'AND',
      conditionGroups: [
        {
          id: uid(),
          logic: 'AND',
          conditions: [
            {
              id: uid(),
              source: defaultSource,
              operator: 'fails',
              value: true,
            },
          ],
        },
      ],
    };
    // Bump the fallback rule's priority so it stays last
    const updated = rules.map((r) =>
      r.id === defaultRuleId ? { ...r, priority: fallbackPriority + 1 } : r,
    );
    onChange([...updated, newRule]);
  }, [rules, gates, onChange, defaultRuleId]);

  return (
    <div className="space-y-4">
      {/* Explanation text */}
      <div className="bg-surface-subtle/60 border border-border-subtle rounded-xl p-4 text-sm text-secondary">
        <p className="font-medium text-primary mb-1">How verdict rules work</p>
        <p>
          Rules are evaluated top-to-bottom (priority 1 first). The first rule whose conditions
          all match determines the verdict. The lowest-priority rule with no conditions acts as
          the default fallback. Combine conditions from quality gates, diagnosis dimensions,
          and performance KPIs.
        </p>
      </div>

      {/* Unified rule settings bar — rolling window always visible, percentile conditional */}
      <RuleSettingsBar
        settings={effectiveSettings}
        onChange={(updated) => onPercentileSettingsChange?.(updated)}
        rollingWindowDays={rollingWindowDays ?? 7}
        onRollingWindowDaysChange={(days) => onRollingWindowDaysChange?.(days)}
        showPercentileSettings={hasPercentileOperators}
      />

      {/* Rule cards */}
      <div className="space-y-3">
        {sortedRules.map((rule, idx) => (
          <RuleCard
            key={rule.id}
            rule={rule}
            index={idx}
            totalRules={sortedRules.length}
            gates={gates}
            dimensions={dimensions}
            selectedKpiIds={selectedKpiIds}
            kpiOptions={kpiOptions}
            defaultScope={effectiveSettings.defaultScope}
            onChange={(updated) => handleRuleChange(rule.id, updated)}
            onDelete={() => handleRuleDelete(rule.id)}
            onMoveUp={() => handleMoveUp(rule.id)}
            onMoveDown={() => handleMoveDown(rule.id)}
            isDefault={rule.id === defaultRuleId}
          />
        ))}
      </div>

      {/* Add rule button */}
      <Button
        variant="secondary"
        size="sm"
        onClick={handleAddRule}
        leftIcon={<Plus className="h-4 w-4" />}
      >
        Add Rule
      </Button>

      {/* Rule count summary */}
      {rules.length > 0 && (
        <p className="text-xs text-muted">
          {rules.length} rule{rules.length !== 1 ? 's' : ''} configured.
          Rules evaluate top-to-bottom; first match wins.
        </p>
      )}
    </div>
  );
}
